<script lang="ts">
import { modalRegister } from '@/services/modals';
import type { Role, Task, TaskGenerator } from '../../../compliance-shared/types/compliance';
import TaskGeneratorUpdateModal from './TaskGeneratorUpdateModal.vue';

export async function taskGeneratorUpdateModal(
  taskGenerator: TaskGenerator,
  fieldsToPushToTasks: string[],
  roles: Role[] | null,
  tasks: Task[],
): Promise<{ update: boolean; tasksIncluded: { include: boolean; task: Task }[] }> {
  return modalRegister({
    content: TaskGeneratorUpdateModal,
    bindings: { taskGenerator, fieldsToPushToTasks, roles, tasks },
  });
}
</script>

<script setup lang="ts">
import BootstrapModal from '@/components/modals/BootstrapModal.vue';
import TaskSmallCard from '@/components/tasks/TaskSmallCard.vue';
import { ref, toRef, watch } from 'vue';

const props = defineProps<{
  taskGenerator: TaskGenerator;
  fieldsToPushToTasks: string[];
  roles: Role[] | null;
  tasks: Task[];
}>();

const tasksIncluded = ref<{ include: boolean; task: Task }[]>([]);
const modal = ref<InstanceType<typeof BootstrapModal> | null>(null);
defineExpose({ modal });

watch(
  toRef(props, 'tasks'),
  function (tasks) {
    tasksIncluded.value = tasks.map(function (task) {
      return { include: true, task };
    });
  },
  { immediate: true },
);
</script>

<template>
  <BootstrapModal
    ref="modal"
    :title="'Update ' + taskGenerator.title"
    :defaultCloseValue="{ update: false, tasksIncluded: [] }"
  >
    <template #default="{ close }">
      <template v-if="tasksIncluded.length > 0">
        <p>The following fields have changed and can be pushed to existing incomplete tasks:</p>

        <ul>
          <li v-for="field of fieldsToPushToTasks" :key="field">{{ field }}</li>
        </ul>

        <p>Which of the following tasks do you want to update?</p>

        <p class="hstack justify-content-start gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="tasksIncluded = tasksIncluded.map((t) => ({ ...t, include: true }))"
          >
            <i class="fa-light fa-check-double"></i> Update all
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="tasksIncluded = tasksIncluded.map((t) => ({ ...t, include: false }))"
          >
            <i class="fa-light fa-xmark"></i> Skip all
          </button>
        </p>

        <div class="vstack gap-1">
          <template v-for="task of tasksIncluded" :key="task.task.taskID">
            <div class="hstack align-items-start gap-1">
              <div>
                <select class="form-select" v-model="task.include" style="min-width: 120px">
                  <option :value="false">Skip</option>
                  <option :value="true">Update</option>
                </select>
              </div>

              <TaskSmallCard class="flex-grow-1" :task="task.task" :roles="roles" />
            </div>
          </template>
        </div>

        <hr />
      </template>

      <div class="hstack justify-content-center gap-3">
        <button class="btn btn-primary" @click="close({ update: true, tasksIncluded })">Update</button>
        <button class="btn btn-secondary" @click="close({ update: false, tasksIncluded: [] })">Cancel</button>
      </div>
    </template>
  </BootstrapModal>
</template>
